import { clamp, round } from "lodash-es";

import type { Sticky as ApiSticky, StickyUpdatedEvent } from "@/backend/natsstubs/board/models";
import { captureMessage } from "@/error/sentry";
import { isEqual } from "@/math/coordinates";
import type { Board } from "@/model/board";
import { isBacklogBoard } from "@/model/board";
import { relativeCoord } from "@/model/coordinates";
import type { LinkedObjective } from "@/model/objective";
import { useAlmItemTypeStore } from "@/store/almItemType";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import type { CardEvent } from "@/store/card";
import { useCardStore } from "@/store/card";
import { useSessionStore } from "@/store/session";
import { useStickyTypeStore } from "@/store/stickyType";
import { useTeamStore } from "@/store/team";
import { firstValue } from "@/utils/record";
import { parseStickerString, stickerToString } from "@/utils/sticker";

import type { ServerSticky } from "../serverModel";
import { mapApiStatusDistribution } from "./mapApiData";
import { mapLinkedSticky, mapReactions, mapStatusDistribution, mapUserId } from "./mapBackendData";

export function serverStickyPos(data: Partial<ServerSticky>) {
  if (data.a_center) {
    return relativeCoord(data.a_center[0], 1 - data.a_center[1]);
  }
}

function apiStickyPos(data: StickyUpdatedEvent) {
  if (data.aCenter) {
    return relativeCoord(data.aCenter[0], 1 - data.aCenter[1]);
  }
}

export function withExplicitDefaults(sticky: ServerSticky): ServerSticky {
  return {
    WSJF_value: 0,
    step_value: 0,
    story_points: 0,
    status: null,
    team_id: null,
    art_id: null,
    alm_issue_id: "",
    alm_issue_url: "",
    alm_source: null,
    flag_type: null,
    risk: "" as const,
    precond_team_id: null,
    precond_team: null,
    depend_team_id: null,
    depend_team: null,
    objectives: [],
    reactions: {},
    assignee: null,
    reporter: null,
    linked_stickies: [],
    status_distribution: null,
    ...sticky,
  };
}

export function changeSticky(board: Board, id?: string, data?: Partial<ServerSticky>) {
  if (id && data) {
    const card = board.cards[id];
    if (!card) {
      return;
    }
    const pos = serverStickyPos(data);
    if (!isBacklogBoard(board.type) && pos && !isEqual(card.meta.pos, pos)) {
      useBoardsStore().setCardPos({ id, boardId: board.id, ...pos });
    }
    if (data.stack_pos !== undefined && data.stack_pos !== card.meta.zIndex) {
      useBoardsStore().cardToFront({ id, boardId: board.id, zIndex: data.stack_pos });
    }

    const cardStore = useCardStore();
    const exist = card.data;
    if (data.text !== undefined && data.text !== exist.text) {
      cardStore.setText({ id, text: data.text });
    }
    if (data.type_id !== undefined && data.type_id !== exist.type.id) {
      cardStore.setType({ id, type: data.type_id });
    }
    if (
      !card.data.type.priorities &&
      data.WSJF_value !== undefined &&
      data.WSJF_value !== exist.priority
    ) {
      cardStore.setPriority({ id, priority: data.WSJF_value ?? 0 });
    }
    if (
      card.data.type.priorities &&
      data.step_value !== undefined &&
      data.step_value !== exist.priority
    ) {
      cardStore.setPriority({ id, priority: data.step_value ?? 0 });
    }
    if (data.story_points !== undefined && data.story_points !== exist.points) {
      cardStore.setPoints({ id, points: data.story_points || 0 });
    }
    if (data.status !== undefined && data.status !== exist.status?.name) {
      cardStore.setStatus(board, id, data.status, { statusClass: data.status_class });
    }
    if (data.team_id !== undefined) {
      const teamId = data.team_id ? "" + data.team_id : null;
      if (teamId !== exist.teamId) {
        cardStore.setTeam({ id, teamId });
      }
    }
    if (data.art_id !== undefined) {
      const artId = data.art_id?.toString() ?? null;
      if (artId !== exist.artId) {
        cardStore.setArt({ id, artId });
      }
    }
    if (
      data.iteration_number !== undefined &&
      // yes null or undefined
      // eslint-disable-next-line eqeqeq
      data.iteration_number != exist.iterationId
    ) {
      cardStore.setIteration({ id, iterationId: data.iteration_number });
    }
    // Update almId and almIssueUrl if they have changed
    // (an updated almId forces an update to the almIssueUrl too)
    if (data.alm_issue_id !== undefined && "" + data.alm_issue_id !== exist.almId) {
      cardStore.setAlmId({ id, almId: "" + data.alm_issue_id });
    }
    if (data.alm_issue_url !== undefined && data.alm_issue_url !== exist.almIssueUrl) {
      cardStore.setAlmIssueUrl({ id, almIssueUrl: data.alm_issue_url });
    }
    if (data.alm_source !== undefined && data.alm_source !== exist.almSourceId) {
      cardStore.setAlmSource({ id, almSourceId: data.alm_source });
    }
    if (data.sticky_group !== undefined && data.sticky_group !== exist.groupId) {
      cardStore.setGroup({ id, groupId: data.sticky_group });
    }
    if (data.flag_type !== undefined && data.flag_type !== stickerToString(exist.flagType)) {
      cardStore.setFlag({ id, flagType: parseStickerString(data.flag_type) });
    }
    if (data.risk !== undefined && data.risk !== exist.risk) {
      cardStore.setRisk({ id, risk: data.risk });
    }
    if (
      data.precond_team_id !== undefined &&
      isTeamIdDifferent(data.precond_team_id, exist.precondTeam?.id)
    ) {
      cardStore.setPrecondTeam({
        id,
        teamId: data.precond_team_id !== null ? "" + data.precond_team_id : null,
      });
    }
    if (data.precond_team !== undefined && !exist.precondTeam) {
      cardStore.setPrecondTeam({ id, teamName: data.precond_team });
    }
    if (
      data.depend_team_id !== undefined &&
      isTeamIdDifferent(data.depend_team_id, exist.dependTeam?.id)
    ) {
      cardStore.setDependTeam({
        id,
        teamId: data.depend_team_id !== null ? "" + data.depend_team_id : null,
      });
    }
    if (data.depend_team !== undefined && !exist.dependTeam) {
      cardStore.setDependTeam({ id, teamName: data.depend_team });
    }
    if (data.objectives !== undefined) {
      cardStore.setObjectives(id, validateObjectives(data.objectives));
    }
    if (data.reactions !== undefined) {
      cardStore.setReactions(id, mapReactions(data.reactions));
    }
    if (data.assignee !== undefined) {
      cardStore.setAssignee(id, mapUserId(data.assignee?.pip_id, data.assignee?.alm_id));
    }
    if (data.reporter !== undefined) {
      cardStore.setReporter(id, mapUserId(data.reporter?.pip_id, data.reporter?.alm_id));
    }
    if (data.linked_stickies) {
      cardStore.setLinkedStickies(id, data.linked_stickies.map(mapLinkedSticky));
    }
    // RPC deltas drop a null status_distribution, so the state can arrive alone
    if (
      Object.hasOwn(data, "status_distribution") ||
      data.status_distribution_state !== undefined
    ) {
      // When the sticky type is changed, the status distribution might have to be removed
      // so the backend will pass { status_distribution: undefined }
      cardStore.setStatusDistribution(
        id,
        data.status_distribution ? mapStatusDistribution(data.status_distribution) : undefined,
        data.status_distribution_state,
      );
    }
  }
}

export function changeApiSticky(board: Board, id?: string, data?: StickyUpdatedEvent) {
  if (id && data) {
    const card = board.cards[id];
    if (!card) {
      return;
    }
    const pos = apiStickyPos(data);
    if (!isBacklogBoard(board.type) && pos && !isEqual(card.meta.pos, pos)) {
      useBoardsStore().setCardPos({ id, boardId: board.id, ...pos });
    }
    if (data.stackPos !== undefined && data.stackPos !== card.meta.zIndex) {
      useBoardsStore().cardToFront({ id, boardId: board.id, zIndex: data.stackPos });
    }

    const cardStore = useCardStore();
    const exist = card.data;
    if (data.text !== undefined && data.text !== exist.text) {
      cardStore.setText({ id, text: data.text });
    }
    if (data.typeId !== undefined && data.typeId !== exist.type.id) {
      cardStore.setType({ id, type: data.typeId });
    }
    if (
      !card.data.type.priorities &&
      data.WSJFValue !== undefined &&
      data.WSJFValue !== exist.priority
    ) {
      cardStore.setPriority({ id, priority: +(data.WSJFValue ?? 0) });
    }
    if (
      card.data.type.priorities &&
      data.stepValue !== undefined &&
      data.stepValue !== exist.priority
    ) {
      cardStore.setPriority({ id, priority: data.stepValue ?? 0 });
    }
    if (data.storyPoints !== undefined && data.storyPoints !== exist.points) {
      cardStore.setPoints({ id, points: data.storyPoints || 0 });
    }
    if (data.status !== undefined && data.status !== exist.status?.name) {
      cardStore.setStatus(board, id, data.status, { statusClass: data.statusClass });
    }
    if (data.teamId !== undefined) {
      const teamId = data.teamId ? "" + data.teamId : null;
      if (teamId !== exist.teamId) {
        cardStore.setTeam({ id, teamId });
      }
    }
    if (data.artId !== undefined) {
      const artId = data.artId?.toString() ?? null;
      if (artId !== exist.artId) {
        cardStore.setArt({ id, artId });
      }
    }
    if (
      data.iterationNumber !== undefined &&
      // yes null or undefined
      // eslint-disable-next-line eqeqeq
      data.iterationNumber != exist.iterationId
    ) {
      cardStore.setIteration({ id, iterationId: data.iterationNumber });
    }
    // Update almId and almIssueUrl if they have changed
    // (an updated almId forces an update to the almIssueUrl too)
    if (data.almIssueId !== undefined && "" + data.almIssueId !== exist.almId) {
      cardStore.setAlmId({ id, almId: "" + data.almIssueId });
    }
    if (data.almIssueUrl !== undefined && data.almIssueUrl !== exist.almIssueUrl) {
      cardStore.setAlmIssueUrl({ id, almIssueUrl: data.almIssueUrl });
    }
    if (data.almSource !== undefined && data.almSource !== exist.almSourceId) {
      cardStore.setAlmSource({ id, almSourceId: data.almSource });
    }
    if (data.stickyGroup !== undefined && data.stickyGroup !== exist.groupId) {
      cardStore.setGroup({ id, groupId: data.stickyGroup });
    }
    if (data.flagType !== undefined && data.flagType !== stickerToString(exist.flagType)) {
      cardStore.setFlag({ id, flagType: parseStickerString(data.flagType) });
    }
    if (data.risk !== undefined && data.risk !== exist.risk) {
      cardStore.setRisk({ id, risk: data.risk });
    }
    if (
      data.precondTeamId !== undefined &&
      isTeamIdDifferent(data.precondTeamId, exist.precondTeam?.id)
    ) {
      cardStore.setPrecondTeam({
        id,
        teamId: data.precondTeamId !== null ? "" + data.precondTeamId : null,
      });
    }
    if (data.precondTeam !== undefined && !exist.precondTeam) {
      cardStore.setPrecondTeam({ id, teamName: data.precondTeam });
    }
    if (
      data.dependTeamId !== undefined &&
      isTeamIdDifferent(data.dependTeamId, exist.dependTeam?.id)
    ) {
      cardStore.setDependTeam({
        id,
        teamId: data.dependTeamId !== null ? "" + data.dependTeamId : null,
      });
    }
    if (data.dependTeam !== undefined && !exist.dependTeam) {
      cardStore.setDependTeam({ id, teamName: data.dependTeam });
    }
    if (data.objectives !== undefined) {
      cardStore.setObjectives(id, validateApiObjectives(data.objectives));
    }
    if (data.reactions !== undefined) {
      cardStore.setReactions(id, mapReactions(data.reactions));
    }
    if (data.assignee !== undefined) {
      cardStore.setAssignee(id, mapUserId(data.assignee?.pipId, data.assignee?.almId));
    }
    if (data.reporter !== undefined) {
      cardStore.setReporter(id, mapUserId(data.reporter?.pipId, data.reporter?.almId));
    }
    if (data.linkedStickies !== undefined) {
      cardStore.setLinkedStickies(id, data.linkedStickies);
    }
    // RPC deltas drop a null status_distribution, so the state can arrive alone
    if (Object.hasOwn(data, "statusDistribution") || data.statusDistributionState !== undefined) {
      // When the sticky type is changed, the status distribution might have to be removed
      // so the backend will pass { status_distribution: undefined }
      cardStore.setStatusDistribution(
        id,
        data.statusDistribution ? mapApiStatusDistribution(data.statusDistribution) : undefined,
        data.statusDistributionState,
      );
    }
  }
}

export function addSticky(data: ServerSticky) {
  const boardsStore = useBoardsStore();
  const board = boardsStore.boards[data.board_id];

  // Log if board doesn't exist in store (for diagnostics)
  // This can legitimately happen when loading linked stickies from unvisited boards
  if (!board) {
    const availableBoardIds = Object.keys(boardsStore.boards);
    const availableBoardTypes = Object.values(boardsStore.boards).map((b) => `${b.id}:${b.type}`);
    const currentBoard = useBoardStore().board;
    const currentSession = useSessionStore().current;
    captureMessage("addSticky: board not found in store", {
      info: {
        missingBoardId: data.board_id,
        stickyId: data.id,
        stickyGroupId: data.sticky_group,
        stickyTypeId: data.type_id,
        availableBoardCount: availableBoardIds.length,
        availableBoardIds,
        availableBoardTypes,
        // Context to help identify race conditions
        currentBoardId: currentBoard?.id,
        currentBoardType: currentBoard?.type,
        currentSessionId: currentSession?.id,
        // DIAGNOSTIC (PI-372) — theory 1: "definitely no board_id passed".
        // Sentry drops undefined keys, so missingBoardId can vanish either way.
        // These string/boolean forms always serialize and disambiguate the cause:
        // a partial cache-hit delta shows stickyKeys="id,<few fields>" with an
        // undefined/null/empty boardIdValue and hasBoardId=false.
        stickyKeys: Object.keys(data).join(","),
        hasBoardId: !!data.board_id,
        hasTypeId: !!data.type_id,
        boardIdValue: String(data.board_id),
        boardIdIsUndefined: data.board_id === undefined,
        boardIdIsNull: data.board_id === null,
        boardIdIsEmpty: data.board_id === "",
        cardInCardStore: !!useCardStore().cards[data.id],
        // DIAGNOSTIC (PI-372) — theory 2: "call stack" — which path calls
        // addSticky with a board-less sticky? (stickyLoader render batch vs live
        // sticky.add subscription vs link/breakdown/issue-selector loaders).
        callStack: new Error("addSticky board not found").stack,
      },
    });
    // Continue adding the sticky - it will have almSourceId: null if board doesn't exist
  }

  const pos = data.a_center || [0.5, 0.5];
  const type = useStickyTypeStore().findStickyType(data.type_id);
  const almSourceId = data.alm_source ?? board?.almSources?.[0]?.id;
  const teamId = data.team_id ? data.team_id.toString() : null;
  const artId = data.art_id?.toString() ?? null;

  const event: CardEvent = {
    id: data.id,
    groupId: data.sticky_group,
    text: data.text || "",
    status: useAlmItemTypeStore().calcStatus(
      data.status,
      type,
      teamId,
      artId,
      almSourceId,
      data.status_class,
    ),
    points: data.story_points || 0,
    boardId: data.board_id,
    type,
    teamId,
    iterationId: data.iteration_number,
    pos: relativeCoord(clamp(pos[0], 0, 1), 1 - clamp(pos[1], 0, 1)),
    zIndex: data.stack_pos,
    priority: type.priorities
      ? type.priorities[data.step_value ?? 0]?.value || firstValue(type.priorities).value
      : round(data.WSJF_value || 0, 2),
    precondTeam: useTeamStore().findTeam({
      id: data.precond_team_id?.toString(),
      name: data.precond_team,
      withExternal: true,
    }),
    dependTeam: useTeamStore().findTeam({
      id: data.depend_team_id?.toString(),
      name: data.depend_team,
      withExternal: true,
    }),
    flagType: parseStickerString(data.flag_type),
    almId: data.alm_issue_id === undefined ? undefined : "" + data.alm_issue_id,
    almSourceId,
    almIssueUrl: data.alm_issue_url,
    risk: data.risk,
    objectives: validateObjectives(data.objectives),
    reactions: data.reactions ? mapReactions(data.reactions) : undefined,
    artId,
    assignee: mapUserId(data.assignee?.pip_id, data.assignee?.alm_id),
    reporter: mapUserId(data.reporter?.pip_id, data.reporter?.alm_id),
    linkedStickies: data.linked_stickies?.map(mapLinkedSticky) || [],
    statusDistribution: data.status_distribution
      ? mapStatusDistribution(data.status_distribution)
      : undefined,
    statusDistributionState: data.status_distribution_state,
  };
  useCardStore().add(event, board);
}

function validateObjectives(objectives: ServerSticky["objectives"]): LinkedObjective[] {
  if (!objectives) {
    return [];
  }
  return objectives.flatMap((objective) => {
    if (objective.id && objective.board_id) {
      return { id: objective.id, boardId: objective.board_id };
    }
    captureMessage("Received invalid objective", { info: { objective } });
    return [];
  });
}

function isTeamIdDifferent(numericTeamId: number | null, stringTeamId: string | undefined) {
  return numericTeamId === null ? stringTeamId !== undefined : "" + numericTeamId !== stringTeamId;
}

export function addApiSticky(data: ApiSticky) {
  const boardsStore = useBoardsStore();
  const board = boardsStore.boards[data.boardId];

  // Log if board doesn't exist in store (for diagnostics)
  // This can legitimately happen when loading linked stickies from unvisited boards
  if (!board) {
    const availableBoardIds = Object.keys(boardsStore.boards);
    const availableBoardTypes = Object.values(boardsStore.boards).map((b) => `${b.id}:${b.type}`);
    const currentBoard = useBoardStore().board;
    const currentSession = useSessionStore().current;
    captureMessage("addApiSticky: board not found in store", {
      info: {
        missingBoardId: data.boardId,
        stickyId: data.id,
        stickyGroupId: data.stickyGroup,
        stickyTypeId: data.typeId,
        availableBoardCount: availableBoardIds.length,
        availableBoardIds,
        availableBoardTypes,
        // Context to help identify race conditions
        currentBoardId: currentBoard?.id,
        currentBoardType: currentBoard?.type,
        currentSessionId: currentSession?.id,
      },
    });
    // Continue adding the sticky - it will have almSourceId: null if board doesn't exist
  }

  const pos = data.aCenter || [0.5, 0.5];
  const type = useStickyTypeStore().findStickyType(data.typeId);
  const almSourceId = data.almSource ?? board?.almSources?.[0]?.id;
  const teamId = data.teamId ? data.teamId.toString() : null;
  const artId = data.artId?.toString() ?? null;

  const event: CardEvent = {
    id: data.id,
    groupId: data.stickyGroup,
    text: data.text || "",
    status: useAlmItemTypeStore().calcStatus(
      data.status,
      type,
      teamId,
      artId,
      almSourceId,
      data.statusClass,
    ),
    points: data.storyPoints || 0,
    boardId: data.boardId,
    type,
    teamId,
    iterationId: data.iterationNumber ?? null,
    pos: relativeCoord(clamp(pos[0], 0, 1), 1 - clamp(pos[1], 0, 1)),
    zIndex: data.stackPos ?? 0,
    priority: type.priorities
      ? type.priorities[data.stepValue!]?.value || firstValue(type.priorities).value
      : round(+(data.WSJFValue ?? 0), 2),
    precondTeam: useTeamStore().findTeam({
      id: data.precondTeamId?.toString(),
      name: data.precondTeam,
      withExternal: true,
    }),
    dependTeam: useTeamStore().findTeam({
      id: data.dependTeamId?.toString(),
      name: data.dependTeam,
      withExternal: true,
    }),
    flagType: parseStickerString(data.flagType),
    almId: data.almIssueId === undefined ? undefined : "" + data.almIssueId,
    almSourceId,
    almIssueUrl: data.almIssueUrl,
    risk: data.risk ?? "",
    objectives: validateApiObjectives(data.objectives),
    reactions: data.reactions ? mapReactions(data.reactions) : undefined,
    artId,
    assignee: mapUserId(data.assignee?.pipId, data.assignee?.almId),
    reporter: mapUserId(data.reporter?.pipId, data.reporter?.almId),
    linkedStickies: data.linkedStickies || [],
    statusDistribution: data.statusDistribution
      ? mapApiStatusDistribution(data.statusDistribution)
      : undefined,
    statusDistributionState: data.statusDistributionState,
  };
  useCardStore().add(event, board);
}

function validateApiObjectives(objectives: ApiSticky["objectives"]): LinkedObjective[] {
  if (!objectives) {
    return [];
  }
  return objectives.flatMap((objective) => {
    if (objective.id && objective.boardId) {
      return { id: objective.id, boardId: objective.boardId };
    }
    captureMessage("Received invalid objective", { info: { objective } });
    return [];
  });
}
